import * as React from 'react';
import {
  NavigationContainer,
  NavigationContainerRef,
} from '@react-navigation/native';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import AuthFlow from './AuthFlow';
import TabBar from './TabBar';
import {Flows, Screens} from './Screens';
import LocalStorage, {LocalStorageType} from 'services/LocalStorage';
import {useUserStore} from 'contexts/StoreContext';
import {Platform} from 'react-native';

const Stack = createNativeStackNavigator();

const config = {
  screens: {
    [Flows.authFlow]: {
      screens: {
        [Screens.LoginScreen.name]: Screens.LoginScreen.link,
      },
    },
    [Flows.tabBarFlow]: {
      screens: {
        [Flows.homeFlow]: {
          screens: {
            [Screens.HomeScreen.name]: Screens.HomeScreen.link,
            [Screens.CreateClassScreen.name]: Screens.CreateClassScreen.link,
            [Screens.ConferenceScreen.name]: Screens.ConferenceScreen.link,
          },
        },
        [Flows.analyticsFlow]: {
          screens: {
            [Screens.AnalyticsScreen.name]: Screens.AnalyticsScreen.link,
          },
        },
      },
    },
  },
};

const linking = {
  prefixes: [
    process.env.NODE_ENV == 'development'
      ? 'http://localhost:3000'
      : 'https://uclass-dev.nixdev.co/',
  ],
  config,
};

interface ContainerProps {}

const Container = (props: ContainerProps) => {
  const [isReady, setIsReady] = React.useState(false);
  const [initialState, setInitialState] = React.useState();
  const [isAuthFlow, setIsAuthFlow] = React.useState<boolean | undefined>();

  const navigationContainerRef =
    React.useRef<NavigationContainerRef<any>>(null);

  const userStore = useUserStore();

  const isReload = () => {
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries.length === 0) {
      return;
    }

    if ((navigationEntries[0] as any).type === 'reload') {
      return true;
    }

    return false;
  };

  const onReady = () => {
    if (isAuthFlow) {
      return;
    }

    if (Platform.OS !== 'web' || isReload()) {
      return;
    }

    navigationContainerRef.current?.reset({
      index: 0,
      routes: [{name: Flows.tabBarFlow}],
    });
    return;
  };

  React.useEffect(() => {
    const getWebNavigation = async () => {
      if (!isReload()) {
        return;
      }

      const state = await LocalStorage.getValue(
        LocalStorageType.navigationState,
      );

      if (state) {
        const parsedState = JSON.parse(state);
        if (parsedState) setInitialState(parsedState);
      }
    };

    const restoreState = async () => {
      if (Platform.OS === 'web') {
        await getWebNavigation();
      }

      const token = await LocalStorage.getValue(LocalStorageType.refreshToken);
      setIsAuthFlow(!token);

      if (token) {
        try {
          await userStore.getCurrentUserIfNeeded();
        } catch (error) {
          if (error === 400) setIsAuthFlow(true);
        }
      }

      setIsReady(true);
    };

    if (!isReady) {
      restoreState();
    }
  }, [isReady]);

  if (!isReady) {
    return null;
  }

  return (
    <NavigationContainer
      ref={navigationContainerRef}
      linking={linking as any}
      initialState={isAuthFlow ? undefined : initialState}
      onReady={onReady}
      onStateChange={state =>
        LocalStorage.save(
          JSON.stringify(state),
          LocalStorageType.navigationState,
        )
      }>
      <Stack.Navigator
        initialRouteName={isAuthFlow ? Flows.authFlow : Flows.tabBarFlow}
        screenOptions={{
          orientation: 'portrait',
        }}>
        <Stack.Screen
          name={Flows.authFlow}
          component={AuthFlow}
          options={{headerShown: false}}
        />
        <Stack.Screen
          name={Flows.tabBarFlow}
          component={TabBar}
          options={{headerShown: false}}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

export default Container;
